<template>
  <div class="versions-layouts-wrapper">
    <transition name="fade">
      <div
        v-if="showModal"
        class="modal-backdrop-custom"
        @click.self="closeModal"
      />
    </transition>

    <transition name="slide-up">
      <div
        v-if="showModal"
        class="modal-custom"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-header-custom">
          <h5 class="modal-title-custom">
            {{ modalTitle }}
          </h5>
          <button
            class="modal-close-btn"
            aria-label="Close"
            type="button"
            @click="closeModal"
          >
            <i class="bi bi-x-lg" />
          </button>
        </div>

        <div class="modal-body-custom">
          <p class="tab-description mb-3">
            {{ modalDescription }}
          </p>
          <div class="d-flex flex-column">
            <input
              v-model="name"
              class="version-input"
              :placeholder="inputPlaceholder"
              type="text"
              @keyup.enter="handleSave"
            >
          </div>
          <div class="d-flex justify-content-end w-100 mt-3">
            <button
              class="show-details-btn text-sm font-medium px-4 mr-1"
              type="button"
              :disabled="isSaving"
              @click="closeModal"
            >
              Cancel
            </button>
            <button
              class="dbh-btn dbh-btn--primary"
              type="button"
              :disabled="isSaving"
              @click="handleSave"
            >
              <i class="bi bi-floppy mr-1" />{{ isSaving ? 'Saving...' : 'Save' }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'NewVersionModal',

  data() {
    return {
      showModal: false,
      mode: 'version',
      name: '',
      isSaving: false,
      beforeSave: null,
      onSaved: null,
      saveOptions: {},
      handleOpenNewVersionModal: null,
    };
  },

  computed: {
    isLayoutMode() {
      return this.mode === 'layout';
    },
    modalTitle() {
      return this.isLayoutMode ? 'Save as new Layout' : 'Save as new Version';
    },
    modalDescription() {
      return this.isLayoutMode
        ? 'Layouts save placements + background only (no products), as a starter.'
        : 'Versions save the full snapshot - placements, background, logos, and products.';
    },
    inputPlaceholder() {
      return this.isLayoutMode ? 'Layout name (optional)' : 'Version name (optional)';
    },
  },

  mounted() {
    this.handleOpenNewVersionModal = (event) => this.openModal(event?.detail || {});
    window.addEventListener('display-builder:new-version-modal-open', this.handleOpenNewVersionModal);
  },

  beforeDestroy() {
    window.removeEventListener('display-builder:new-version-modal-open', this.handleOpenNewVersionModal);
  },

  methods: {
    openModal(options = {}) {
      this.mode = options.type === 'layout' ? 'layout' : 'version';
      this.name = '';
      this.beforeSave = typeof options.beforeSave === 'function' ? options.beforeSave : null;
      this.onSaved = typeof options.onSaved === 'function' ? options.onSaved : null;
      this.saveOptions = options.saveOptions && typeof options.saveOptions === 'object'
        ? options.saveOptions
        : {};
      this.showModal = true;
      this.$nextTick(() => {
        this.$el.querySelector('.version-input')?.focus();
      });
    },
    closeModal() {
      if (this.isSaving) {
        return;
      }

      this.showModal = false;
      this.beforeSave = null;
      this.onSaved = null;
      this.saveOptions = {};
    },
    async handleSave() {
      if (this.isSaving) {
        return;
      }

      this.isSaving = true;

      try {
        if (this.beforeSave) {
          await this.beforeSave();
        }

        const item = this.isLayoutMode
          ? await window.displayBuilderSaveLayoutPreset?.({
            name: this.name,
            forceNew: true,
            ...this.saveOptions,
          })
          : await window.displayBuilderSaveCurrentVersion?.({ name: this.name, forceNew: true });

        if (this.onSaved) {
          await this.onSaved(item);
        }

        this.showModal = false;
        this.beforeSave = null;
        this.onSaved = null;
        this.saveOptions = {};
      } catch (error) {
        console.error('[DisplayBuilder] Save failed', error);
        toastr.error(this.isLayoutMode ? 'Could not save layout' : 'Could not save version');
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.versions-layouts-wrapper {
  position: relative;
  display: inline-block;
  font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
}

.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(15, 15, 25, 0.45);
  z-index: 1040;
}

.modal-custom {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-width: 28rem;
  width: calc(100% - 32px);
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.18);
  z-index: 1050;
  overflow: hidden;
}

.modal-header-custom {
  display: flex;
  align-items: center;
  padding: 18px 20px 8px;
  gap: 8px;
}

.modal-title-custom {
  font-size: 20px;
  font-weight: 700;
  color: #080e1d;
  margin: 0;
  flex: 1;
}

.modal-close-btn {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #4b5563;
  display: flex;
  align-items: center;
  border-radius: 4px;
}

.modal-close-btn:hover {
  color: #111827;
  background: #f3f4f6;
}

.modal-body-custom {
  padding: 16px 20px 20px;
}

.tab-description {
  font-size: 13px;
  color: #667085;
  margin: 0 0 12px;
}

.show-details-btn {
  background: none;
  border: none;
  color: #080e1d;
  cursor: pointer;
  border-radius: 4px;
  transition: color 0.12s, background 0.12s;
  height: 2.5rem;
}

.show-details-btn:hover {
  background: #f3f4f6;
}

.dbh-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  border: 1px solid transparent;
  padding: 0.5rem 1rem;
  cursor: pointer;
  white-space: nowrap;
  line-height: 1;
}

.dbh-btn--primary {
  background: #5bc0a4;
  border-color: #5bc0a4;
  color: #fff;
}

.dbh-btn:disabled,
.show-details-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.version-input {
  padding: 0.5rem 0.75rem;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  height: 2.25rem;
  width: 100%;
  color: #080e1d;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.slide-up-enter,
.slide-up-leave-to {
  opacity: 0;
  transform: translate(-50%, calc(-50% + 12px));
}
</style>
